import React, { useCallback, useContext, useMemo } from 'react'
import cn from 'clsx'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { rem } from 'polished'
import { BundleOfferPackageOptionView } from 'selectors/offerPageSelectors'
import styled from 'styled-components'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import Pill from 'components/Luxkit/Pill'
import { isLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import BundleAndSaveBookButton from './BundleAndSaveBookButton'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { getInstalmentsAbTestEnabled } from 'selectors/featuresSelectors'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { sum } from 'lib/array/arrayUtils'
import { BundledOfferState, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { getBundleValueUpView } from 'lib/bundleAndSave/getBundleValueUpView'
import { getBundlePropertiesFees } from 'lib/bundleAndSave/getBundlePropertiesFees'
import BodyText from 'components/Luxkit/Typography/BodyText'

const PackagePrice = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;

  & + & {
    margin-left: ${rem(12)};
  }
`

const PriceButton = styled(BundleAndSaveBookButton)`
  margin-top: ${rem(12)};
`

const PackagePrices = styled(Caption)`
  display: flex;
  position: relative;

  &.show-discount {
    margin-top: ${rem(8)};
  }

  > * {
    flex: 1 1 calc(50% - ${rem(12)});
  }

  > * + * {
    padding-left: ${rem(12)};
    border-left: 1px solid ${props => props.theme.palette.neutral.five};
    flex: 1 1 50%;
  }
`

const DurationCaption = styled(Caption)`
  margin-top: ${rem(8)};
`

const TagWrapper = styled.div`
  display: flex;
  gap: ${rem(4)};
`

interface Props {
  offer: App.BundleOffer;
  bundledOffersState: BundledOfferState;
  packageView: BundleOfferPackageOptionView;
  rooms: Array<App.Occupants>;
  fetching?: boolean;
  hidePartnership?: boolean;
  availableForPurchase: boolean;
  currency: string;
  instalmentsABTestEnabled: boolean;
  defaultDepositAmountPercentage: number;
}

function BundleAndSaveOptionPriceDetails(props: Props) {
  const {
    offer,
    bundledOffersState,
    packageView,
    rooms,
    fetching,
    availableForPurchase,
  } = props

  const allPackages = useMemo(() => {
    return (packageView?.options ?? []).map(p => p.package)
  }, [packageView])

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const { showTaxExclusivePrices } = useContext(GeoContext)

  const checkInDates = Object.values(bundledOffersState).map((o) => o.checkInDate)
  const fromCalendarInfo = packageView?.options.every((o) => o.fromCalendarInfo)

  const hotelAvailable = packageView.hotelAvailable
  const canPurchase = availableForPurchase && packageView.capacityValid && hotelAvailable && !fetching
  const canBuyNow = !checkInDates.includes(undefined) && !!rooms && fromCalendarInfo && !fetching

  const showIncludedGuestsExceeded = useMemo(() => allPackages.some((pkg) => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer.bundledOffers[pkg.offerId])), [allPackages, rooms, offer])
  const roomPolicyDescription = useMemo(() => allPackages.find((pkg) => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer.bundledOffers[pkg.offerId]))?.roomPolicyDescription, [allPackages, rooms, offer])

  const {
    hotelPrice,
    taxesAndFees,
    propertyFees,
  } = packageView

  const propertiesFees = getBundlePropertiesFees(offer, packageView)

  const { showPriceDetails, shouldShowDiscountPercent, hotelDiscountPercent } = getBundleValueUpView(offer, packageView)

  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)

  const totalPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees

  const duration = useMemo(() => {
    return sum(allPackages, (p) => p.duration)
  }, [allPackages])

  const onSubmit = useCallback(() => {
    if (!canBuyNow) {
      trackEvent('CHECK_AVAILABILITY_PRESSED')
    } else {
      trackEvent('BOOK_NOW_PRESSED')
    }
  }, [canBuyNow, trackEvent])

  return (<div>
    <TagWrapper>
      {shouldShowDiscountPercent && canPurchase &&
        <ValueWrapper variant="medium" weight="bold">
          <Pill kind="primary" variant="success">Save up to {hotelDiscountPercent}%</Pill>
        </ValueWrapper>
        }
    </TagWrapper>
    <PackagePrices variant="medium" className={cn({ 'show-discount': shouldShowDiscountPercent })}>
      {!offer.bundledWithFlightsOnly &&
        <PackagePrice data-testid="pricing">
          {canPurchase && <div>
            <DurationCaption variant="medium">Total for {pluralizeToString('night', duration)}, 2 hotels {!checkInDates[0] && 'from'}</DurationCaption>
            <Heading variant="heading6" colour={exclusivePriceColor}>
              <FormatCurrency value={totalPrice} format="roundedDollar" />
            </Heading>
            <TaxesAndFees
                taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
                taxAmount={taxesAndFees}
              />
            {showIncludedGuestsExceeded && (
              <AdditionalGuestsPopup
                complex={false}
                description="+ Extra guest surcharge"
                modalContent={roomPolicyDescription}
              />
            )}
            {showPriceDetails &&
              <PriceDetailsModal
                  offerPage
                  offer={offer}
                  duration={duration}
                  taxesAndFees={taxesAndFees}
                  propertyFees={propertyFees}
                  propertiesFees={propertiesFees}
                  hotelPrice={hotelPrice}
                  rooms={rooms}
                  extraGuestMessage={showIncludedGuestsExceeded ? roomPolicyDescription : ''}
                  cta={
                    <BundleAndSaveBookButton
                      type="submit"
                      disabled={fetching}
                      packageView={packageView}
                      onSubmit={onSubmit}
                    >
                      {canBuyNow ? 'Book now' : 'Check availability'}
                    </BundleAndSaveBookButton>
                  }
                />
              }
          </div>}

          {availableForPurchase && canPurchase && <PriceButton
              data-testid="HotelBookingBtn"
              type="submit"
              disabled={fetching}
              packageView={packageView}
              onSubmit={onSubmit}
            >
            {canBuyNow ? 'Book now' : 'Check availability'}
          </PriceButton>}
          {availableForPurchase && !canPurchase && (
            <div>
              {hotelAvailable && !packageView.capacityValid && (
                <Caption variant="large" align="center">
                  Max number of guests exceeded.
                  <br/>
                </Caption>
              )}

              {!hotelAvailable && (
                <BodyText variant="medium">
                  Sold out for your selected dates.
                </BodyText>
              )}
            </div>
          )}
        </PackagePrice>
        }

      <LoadingIndicator visible={fetching} floating opaque />
    </PackagePrices>
  </div>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  currency: appState.geo.currentCurrency,
  bundledOffersState: state.bundledOffers,
  rooms: state.rooms,
  offer: state.offer,
  isLoggedIn: isLoggedInSelector(appState),
  instalmentsABTestEnabled: getInstalmentsAbTestEnabled(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}))(BundleAndSaveOptionPriceDetails)
